import {
  Download,
  Loader2,
  MessageCircle,
  Pause,
  Play,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Comment } from "@/types/types";
import { Doc } from "@cvx/_generated/dataModel";
import { VideoVersion } from "@/types/types";
import { formatTime } from "@/utils/format";

interface VideoPlayerProps {
  version: VideoVersion;
  videoDuration: number | null;
  comments: Comment[];
  onTimeUpdate: (time: number) => void;
  currentTime: number;
  selectedRange: { start: number; end: number } | null;
  onRangeSelect: (range: { start: number; end: number } | null) => void;
  onShowCommentForm: (
    time: number,
    position: { x: number; y: number },
    range?: { start: number; end: number },
  ) => void;
  onDurationLoaded?: (duration: number) => void;
  onExposeSeekFunction: (seekFn: (time: number) => void) => void;
  activeDrawing?: string | null;
  onDrawingDisplay?: (drawing: string | null) => void;
  onExposeVideoFrame?: (
    getFrameFn: (targetTimestamp?: number) => string | null,
  ) => void;
}

export const VideoPlayer = ({
  version,
  videoDuration,
  comments,
  onTimeUpdate,
  currentTime,
  selectedRange,
  onRangeSelect,
  onShowCommentForm,
  onDurationLoaded,
  onExposeSeekFunction,
  activeDrawing,
  onDrawingDisplay,
  onExposeVideoFrame,
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [commentMode, setCommentMode] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  // Helper function to calculate popup position based on timestamp
  const calculatePopupPosition = (
    time: number,
    range?: { start: number; end: number },
  ) => {
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect || !videoDuration) {
      return { x: 100, y: 100 }; // Fallback position
    }

    // Use the end of range if available, otherwise use the single timestamp
    const targetTime = range ? range.end : time;
    const timelineProgress = targetTime / videoDuration;

    // Calculate x position on timeline
    const timelineX = rect.left + timelineProgress * rect.width;

    // Position popup above and slightly to the left of the timeline position
    return {
      x: Math.max(20, timelineX - 160), // 160px is roughly half the popup width
      y: rect.top - 220, // Position well above the timeline
    };
  };

  const seekTo = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const handleDownloadVideo = async () => {
    if (!version?.videoUrl || isDownloading) return;

    setIsDownloading(true);

    try {
      // Create download link directly from video URL
      const link = document.createElement("a");
      link.href = version.videoUrl;
      link.download = `video_${version.id}.mp4`;
      link.target = "_blank"; // Open in new tab as fallback
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading video:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const captureVideoFrame = (targetTimestamp?: number): string | null => {
    const video = videoRef.current;
    if (!video) {
      console.log("No video element found");
      return null;
    }

    // Accept readyState 1 (metadata loaded) or higher
    if (video.readyState < 1) {
      console.log("Video not ready, readyState:", video.readyState);
      return null;
    }

    // Use the target timestamp if provided, otherwise use current time
    const timestamp =
      targetTimestamp !== undefined ? targetTimestamp : video.currentTime;

    // Video info that can be used to recreate the frame
    const videoInfo = {
      src: version.videoUrl,
      currentTime: timestamp, // Use the specific timestamp for the comment
      videoWidth: video.videoWidth || 640, // fallback dimensions
      videoHeight: video.videoHeight || 360,
      aspectRatio:
        video.videoWidth && video.videoHeight
          ? video.videoWidth / video.videoHeight
          : 16 / 9,
    };

    console.log("Captured video info for timestamp", timestamp, ":", videoInfo);
    return JSON.stringify(videoInfo);
  };

  // Expose functions to parent component
  useEffect(() => {
    onExposeSeekFunction(seekTo);
    if (onExposeVideoFrame) {
      onExposeVideoFrame(captureVideoFrame);
    }
  }, [onExposeSeekFunction, onExposeVideoFrame]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      onTimeUpdate(video.currentTime);
    };

    const handleLoadStart = () => {
      setIsVideoLoading(true);
    };

    const handleLoadedMetadata = () => {
      setIsVideoLoading(false);
      onTimeUpdate(0);
      // Emit the actual video duration
      if (onDurationLoaded && video.duration) {
        onDurationLoaded(video.duration);
      }
    };

    const handleGlobalMouseUp = (_e: MouseEvent) => {
      if (isDragging && commentMode && dragStart !== null) {
        // If we're dragging in comment mode and mouse is released anywhere,
        // create comment with current range
        let finalTime = dragStart;
        const finalRange = selectedRange;

        // If we have a meaningful range, use it
        if (
          selectedRange &&
          Math.abs(selectedRange.end - selectedRange.start) > 0.5
        ) {
          finalTime = (selectedRange.start + selectedRange.end) / 2;
        }

        // Calculate popup position based on timestamp, not mouse position
        const popupPosition = calculatePopupPosition(
          finalTime,
          finalRange || undefined,
        );

        if (finalRange) {
          onShowCommentForm(finalTime, popupPosition, finalRange);
        } else {
          onShowCommentForm(finalTime, popupPosition);
        }

        setIsDragging(false);
        setDragStart(null);
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadstart", handleLoadStart);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    document.addEventListener("mouseup", handleGlobalMouseUp);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadstart", handleLoadStart);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [
    onTimeUpdate,
    onDurationLoaded,
    isDragging,
    commentMode,
    dragStart,
    selectedRange,
    onShowCommentForm,
  ]);

  // Reset loading state when version changes
  useEffect(() => {
    setIsVideoLoading(true);
  }, [version.id]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimelineMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;

    const clickX = e.clientX - rect.left;
    const timelineWidth = rect.width;
    const duration = videoDuration || 0;
    const clickTime = Math.max(
      0,
      Math.min(duration, (clickX / timelineWidth) * duration),
    );

    setDragStart(clickTime);
    onRangeSelect(null); // Clear any existing selection
  };

  const handleTimelineMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || dragStart === null) return;

    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;

    const currentX = e.clientX - rect.left;
    const timelineWidth = rect.width;
    const duration = videoDuration || 0;
    const currentTime = Math.max(
      0,
      Math.min(duration, (currentX / timelineWidth) * duration),
    );

    if (commentMode) {
      const start = Math.min(dragStart, currentTime);
      const end = Math.max(dragStart, currentTime);

      // Only update range if there's a meaningful difference (more than 0.1 seconds)
      if (Math.abs(end - start) > 0.1) {
        onRangeSelect({
          start,
          end,
        });
      } else {
        // Clear range for very small selections
        onRangeSelect(null);
      }
    }
  };

  const handleTimelineMouseUp = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;

    const clickX = e.clientX - rect.left;
    const timelineWidth = rect.width;
    const duration = videoDuration || 0;
    const clickTime = Math.max(
      0,
      Math.min(duration, (clickX / timelineWidth) * duration),
    );

    if (commentMode) {
      // Comment mode: Show comment form
      if (
        dragStart !== null &&
        selectedRange &&
        Math.abs(selectedRange.end - selectedRange.start) > 0.5
      ) {
        // If we have a meaningful range (more than 0.5 seconds), show comment form with range
        const rangeCenter = (selectedRange.start + selectedRange.end) / 2;
        const popupPosition = calculatePopupPosition(
          rangeCenter,
          selectedRange,
        );

        onShowCommentForm(rangeCenter, popupPosition, selectedRange);
      } else {
        // Single click - show comment form for specific time
        if (videoRef.current) {
          videoRef.current.currentTime = clickTime;
        }
        const popupPosition = calculatePopupPosition(clickTime);
        onShowCommentForm(clickTime, popupPosition);
      }
    } else {
      // Normal mode: Just seek to the clicked position
      if (videoRef.current) {
        videoRef.current.currentTime = clickTime;
      }
      // Clear any range selection when not in comment mode
      onRangeSelect(null);
    }

    setIsDragging(false);
    setDragStart(null);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const progressPercentage = videoDuration
    ? (currentTime / videoDuration) * 100
    : 0;

  // Automatic drawing display based on timestamp
  useEffect(() => {
    if (!videoDuration || !onDrawingDisplay) return;

    // Filter out deleted comments and find comment with drawing that matches current time
    const visibleComments = comments.filter(
      (comment) => comment.message !== "This comment has been deleted",
    );
    const activeComment = visibleComments.find((comment) => {
      if (!comment.drawing) return false;

      const start = comment.timestampRange.start;
      const end = comment.timestampRange.end || start + 1; // Show for 1 second if no end time

      return currentTime >= start && currentTime <= end;
    });

    if (activeComment?.drawing) {
      onDrawingDisplay(activeComment.drawing);
    } else if (!activeComment) {
      // Clear drawing if no active comment
      onDrawingDisplay(null);
    }
  }, [currentTime, comments, videoDuration, onDrawingDisplay]);

  // Filter out deleted comments for timeline markers
  const visibleComments = comments.filter(
    (comment) => comment.message !== "This comment has been deleted",
  );

  return (
    <div className="bg-black rounded-lg overflow-hidden h-full flex flex-col">
      <div
        ref={videoContainerRef}
        className="relative flex-1 flex items-center justify-center min-h-0"
      >
        <video
          ref={videoRef}
          src={version.videoUrl}
          className="max-h-[500px] max-w-full aspect-[9/16] object-cover cursor-pointer"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onClick={togglePlay}
          crossOrigin="anonymous"
          preload="auto"
        />

        {/* Drawing Overlay */}
        {activeDrawing && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              className="max-h-[500px] max-w-full aspect-[9/16] relative"
              style={{
                width: videoRef.current?.clientWidth || "100%",
                height: videoRef.current?.clientHeight || "100%",
              }}
            >
              <div
                className="absolute inset-0 opacity-80"
                dangerouslySetInnerHTML={{ __html: activeDrawing }}
                style={{
                  width: "100%",
                  height: "100%",
                }}
              />
              {/* Close drawing overlay button */}
              <button
                onClick={() => onDrawingDisplay?.(null)}
                className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70 pointer-events-auto"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {(isVideoLoading || videoDuration === null) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              <span className="text-white text-sm font-medium">
                Loading video...
              </span>
            </div>
          </div>
        )}

        {/* Play overlay for when video is paused */}
        {!isPlaying &&
          !isVideoLoading &&
          videoDuration !== null &&
          !activeDrawing && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-black/20 cursor-pointer"
              onClick={togglePlay}
            >
              <div className="bg-white/90 rounded-full p-4">
                <Play className="h-8 w-8 text-black" />
              </div>
            </div>
          )}
      </div>

      {/* Custom Controls - Fixed at bottom */}
      <div className="bg-gray-900 p-4 space-y-3 flex-shrink-0">
        {/* Timeline */}
        <div className="space-y-2">
          <div
            ref={timelineRef}
            className={`relative h-6 bg-gray-700 rounded ${commentMode ? "cursor-crosshair" : "cursor-pointer"}`}
            onMouseDown={handleTimelineMouseDown}
            onMouseMove={handleTimelineMouseMove}
            onMouseUp={handleTimelineMouseUp}
            onMouseLeave={() => {
              // Only cancel dragging if not in comment mode
              // In comment mode, let the global mouseup handler deal with it
              if (!commentMode) {
                setIsDragging(false);
                setDragStart(null);
              }
            }}
          >
            {/* Progress bar */}
            <div
              className="absolute top-0 left-0 h-full bg-blue-500 rounded"
              style={{ width: `${progressPercentage}%` }}
            />

            {/* Selected range */}
            {selectedRange && videoDuration && (
              <div
                className="absolute top-0 h-full bg-yellow-400/50 rounded border-2 border-yellow-400"
                style={{
                  left: `${(selectedRange.start / videoDuration) * 100}%`,
                  width: `${((selectedRange.end - selectedRange.start) / videoDuration) * 100}%`,
                }}
              />
            )}

            {/* Range selection preview during drag */}
            {isDragging && dragStart !== null && videoDuration && (
              <div
                className="absolute top-0 h-full bg-blue-400/30 rounded border-2 border-dashed border-blue-400"
                style={{
                  left: `${(Math.min(dragStart, currentTime) / videoDuration) * 100}%`,
                  width: `${(Math.abs(currentTime - dragStart) / videoDuration) * 100}%`,
                }}
              />
            )}

            {/* Comment markers - only show visible (non-deleted) comments */}
            {videoDuration &&
              visibleComments.map((comment) => (
                <div
                  key={comment._id}
                  className={`absolute top-0 w-1 h-full cursor-pointer hover:bg-red-400 transition-colors ${comment.drawing ? "bg-orange-500" : "bg-red-500"
                    }`}
                  style={{
                    left: `${(comment.timestampRange.start / (videoDuration || 0)) * 100}%`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    seekTo(comment.timestampRange.start);
                    // If comment has drawing, show it
                    if (comment.drawing && onDrawingDisplay) {
                      onDrawingDisplay(comment.drawing);
                    }
                  }}
                  title={`${(comment.author as Doc<"users">).name?.split(" ")[0]}: ${comment.message.substring(0, 50)}${comment.message.length > 50 ? "..." : ""}${comment.drawing ? " (has drawing)" : ""}`}
                >
                  {/* Drawing indicator */}
                  {comment.drawing && (
                    <div className="absolute -top-1 left-0 w-1 h-1 bg-orange-300 rounded-full"></div>
                  )}
                </div>
              ))}

            {/* Current time indicator */}
            <div
              className="absolute top-0 w-1 h-full bg-white shadow-lg"
              style={{ left: `${progressPercentage}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>{formatTime(currentTime)}</span>
            {selectedRange && (
              <span className="text-yellow-400">
                Selected: {formatTime(selectedRange.start)} -{" "}
                {formatTime(selectedRange.end)}
              </span>
            )}
            <span>{videoDuration ? formatTime(videoDuration) : "--:--"}</span>
          </div>
        </div>

        {/* Control buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={togglePlay}
              className="text-white hover:bg-white/10"
            >
              {isPlaying ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCommentMode(!commentMode)}
              className={`text-white hover:bg-white/10 ${commentMode ? "bg-blue-500/20 text-blue-400" : ""}`}
              title={commentMode ? "Exit comment mode" : "Enter comment mode"}
            >
              <MessageCircle className="h-4 w-4" />
            </Button>

            <Button
              size="sm"
              variant="ghost"
              onClick={handleDownloadVideo}
              disabled={isDownloading}
              className="text-white hover:bg-white/10 disabled:opacity-50"
              title={isDownloading ? "Downloading..." : "Download video"}
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </Button>

            <Button
              size="sm"
              variant="ghost"
              onClick={toggleMute}
              className="text-white hover:bg-white/10"
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>

            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className="w-16 h-1 bg-gray-700 rounded slider"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
